import { useEffect, useState } from "react";
import {
  useParams,
  Link,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { api, Technique, Lesson, LessonProgress } from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Play,
  GraduationCap,
  BookOpen,
  CheckCircle2,
  XCircle,
  Trophy,
  ArrowRight,
  RotateCcw,
} from "lucide-react";
import StepByStepPractice from "../components/StepByStepPractice";
import { getTechniqueBreakdown } from "../data/techniqueBreakdowns";
import { useToast } from "@/hooks/use-toast";
import {
  TechniqueChatProvider,
  TechniqueChatTrigger,
  TechniqueChatPanel,
} from "@/components/chat";
import StructuredExplanation from "@/components/StructuredExplanation";
import { isStructuredExplanation } from "@/types/structuredExplanation";

export default function TechniqueDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams.get("lessonId");
  const navigate = useNavigate();
  const { toast } = useToast();

  const [technique, setTechnique] = useState<Technique | null>(null);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [lessonProgress, setLessonProgress] = useState<LessonProgress | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [nextLessonId, setNextLessonId] = useState<string | null>(null);
  const [startTime] = useState(Date.now());

  // State for interactive practice problems
  const [practiceAnswers, setPracticeAnswers] = useState<{
    [key: number]: string;
  }>({});
  const [practiceResults, setPracticeResults] = useState<{
    [key: number]: { submitted: boolean; correct: boolean };
  }>({});

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      try {
        setLoading(true);

        // Load technique data
        const techniqueData = await api.getTechnique(id);
        setTechnique(techniqueData);

        // Load lesson data if lessonId is present
        if (lessonId) {
          try {
            const lessonData = await api.startLesson(lessonId);
            setLesson(lessonData.lesson);
            setLessonProgress(lessonData.progress);

            // Check if we already have a next lesson queued from previous completion
            // (Note: The API doesn't return next_lesson_id on start, only submit.
            // We could potentially fetch it if completed, but user can just hit back to roadmap)
          } catch (lessonErr) {
            console.error("Failed to load lesson context:", lessonErr);
            toast({
              title: "Lesson Loading Error",
              description: "Could not load lesson tracking information.",
              variant: "destructive",
            });
          }
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load technique",
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, lessonId]);

  const handleCompleteLesson = async () => {
    if (!lessonId || !lesson) return;

    try {
      setSubmitting(true);
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);

      const result = await api.submitLessonAnswer(
        lessonId,
        1, // Learn lessons count as 1 correct problem
        timeSpent,
        1, // 1 problem attempted
      );

      setLessonProgress(result.progress);
      setNextLessonId(result.next_lesson_id);

      if (result.lesson_completed) {
        toast({
          title: "Lesson Completed!",
          description: "You've mastered the theory. Ready for practice?",
        });
      }
    } catch (err) {
      console.error("Failed to complete lesson:", err);
      toast({
        title: "Error",
        description: "Failed to save lesson progress.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (nextLessonId) {
      // Pass along the returnTo parameter to the next lesson
      const returnTo = searchParams.get("returnTo");
      const returnParam = returnTo
        ? `?returnTo=${encodeURIComponent(returnTo)}`
        : "";
      navigate(`/lesson/${nextLessonId}${returnParam}`);
    } else {
      // If there's a returnTo parameter, go back there; otherwise go to learning-hub
      const returnTo = searchParams.get("returnTo");
      navigate(returnTo || "/learning-hub");
    }
  };

  const handlePracticeAnswerChange = (index: number, value: string) => {
    setPracticeAnswers((prev) => ({ ...prev, [index]: value }));
  };

  const handleCheckAnswer = (index: number, correctSolution: string) => {
    const userAnswer = (practiceAnswers[index] || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");
    const correct = correctSolution.trim().toLowerCase().replace(/\s+/g, "");
    const isCorrect = userAnswer === correct;

    setPracticeResults((prev) => ({
      ...prev,
      [index]: { submitted: true, correct: isCorrect },
    }));
  };

  const handleRetryProblem = (index: number) => {
    setPracticeAnswers((prev) => ({ ...prev, [index]: "" }));
    setPracticeResults((prev) => {
      const newResults = { ...prev };
      delete newResults[index];
      return newResults;
    });
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  if (error || !technique) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-red-600">
              {error || "Technique not found"}
            </p>
            <div className="flex justify-center mt-4">
              <Link to="/learning-hub">
                <Button>Back to Learning Hub</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const breakdown = getTechniqueBreakdown(technique.id);
  const isLessonMode = !!lesson && !!lessonId;
  const isCompleted = lessonProgress?.status === "completed";

  // Prepare technique object for chat provider
  const chatTechnique = technique
    ? {
        id: technique.id,
        name: technique.name,
        category: technique.category,
        description: technique.description,
      }
    : null;

  return (
    <TechniqueChatProvider technique={chatTechnique}>
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Link
          to={
            isLessonMode && searchParams.get("returnTo")
              ? searchParams.get("returnTo")!
              : "/learning-hub"
          }
        >
          <Button variant="ghost" className="mb-6 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            {isLessonMode ? "Back to Roadmap" : "Back to Learning Hub"}
          </Button>
        </Link>

        {/* Lesson Context Banner */}
        {isLessonMode && (
          <Card className="mb-6 border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge
                    variant="outline"
                    className="bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-100 border-blue-200"
                  >
                    Lesson {lesson?.lesson_number}
                  </Badge>
                  <CardTitle className="text-xl">{lesson?.title}</CardTitle>
                </div>
                {isCompleted ? (
                  <Badge className="bg-green-500 hover:bg-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Completed
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200"
                  >
                    In Progress
                  </Badge>
                )}
              </div>
              <CardDescription className="mt-2 text-blue-700 dark:text-blue-300">
                Completion Criteria: Read through the material and understand
                the technique.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-3xl mb-2">
                  {technique.name}
                </CardTitle>
                <CardDescription className="text-base">
                  {technique.description}
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Badge className="text-sm">{technique.difficulty}</Badge>
              {technique.category && (
                <Badge variant="outline" className="text-sm">
                  {technique.category.replace(/_/g, " ")}
                </Badge>
              )}
              {technique.level && (
                <Badge
                  variant="outline"
                  className="text-sm bg-blue-50 text-blue-700"
                >
                  Level {technique.level}
                </Badge>
              )}
            </div>
          </CardHeader>
        </Card>

        <Tabs defaultValue="learn" className="space-y-6">
          <TabsList
            className={`grid w-full ${breakdown ? "grid-cols-2" : "grid-cols-1"}`}
          >
            <TabsTrigger value="learn" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              {breakdown ? "Theory & Examples" : "Learn"}
            </TabsTrigger>
            {breakdown && (
              <TabsTrigger value="tutorial" className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                Interactive Tutorial
              </TabsTrigger>
            )}
          </TabsList>

          {breakdown && (
            <TabsContent value="tutorial" className="space-y-6">
              <StepByStepPractice
                techniqueName={technique.name}
                steps={breakdown.steps}
                finalExample={breakdown.finalExample}
              />
            </TabsContent>
          )}

          <TabsContent value="learn" className="space-y-6">
            {technique.video_url && (
              <Card>
                <CardHeader>
                  <CardTitle>Video Tutorial</CardTitle>
                  <CardDescription>
                    Watch a visual explanation before reading the breakdown.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
                    <video
                      controls
                      className="w-full h-full"
                      preload="metadata"
                    >
                      <source src={technique.video_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Render structured or plain text explanation */}
            {/* Use enhanced_explanation if available, otherwise fall back to explanation */}
            {technique.enhanced_explanation &&
            isStructuredExplanation(technique.enhanced_explanation) ? (
              <StructuredExplanation
                explanation={technique.enhanced_explanation}
              />
            ) : typeof technique.explanation === "object" &&
              isStructuredExplanation(technique.explanation) ? (
              <StructuredExplanation explanation={technique.explanation} />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>How It Works</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {(String(technique.explanation) || "")
                      .split(/(?<=[.!?])\s+/)
                      .filter((s) => s.trim().length > 0)
                      .map((sentence, index) => (
                        <li
                          key={index}
                          className="flex gap-3 items-start text-gray-700 dark:text-gray-300 leading-relaxed"
                        >
                          <div className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                          <span>{sentence}</span>
                        </li>
                      ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Why It Works</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {(technique.why_it_works || "")
                    .split(/(?<=[.!?])\s+/)
                    .filter((s) => s.trim().length > 0)
                    .map((sentence, index) => (
                      <li
                        key={index}
                        className="flex gap-3 items-start text-gray-700 dark:text-gray-300 leading-relaxed"
                      >
                        <div className="mt-2 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                        <span>{sentence}</span>
                      </li>
                    ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Examples</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {technique.examples.map((example, index) => (
                    <div
                      key={index}
                      className="border-l-4 border-indigo-500 pl-4"
                    >
                      <div className="mb-2">
                        <span className="font-semibold">Problem: </span>
                        <span className="font-mono text-lg">
                          {example.problem}
                        </span>
                      </div>
                      <div className="mb-2">
                        <span className="font-semibold">Solution: </span>
                        <span className="font-mono text-lg text-green-600">
                          {example.solution}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-semibold">Steps: </span>
                        {example.steps}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Now Practice / Completion Section */}
        <Card className="mt-6 border-indigo-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-indigo-600" />
              Ready to Practice?
            </CardTitle>
            <CardDescription>
              You've reviewed the theory. Now put it to the test!
            </CardDescription>
          </CardHeader>
          <CardContent className="py-6 space-y-4">
            {/* Show lesson practice problems if available */}
            {isLessonMode &&
              lesson?.example_problems &&
              lesson.example_problems.length > 0 && (
                <div className="space-y-4 mb-6">
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300">
                    Try These Practice Problems:
                  </h4>
                  <div className="space-y-4">
                    {lesson.example_problems.map((problem, index) => {
                      const result = practiceResults[index];
                      const isSubmitted = result?.submitted;
                      const isCorrect = result?.correct;

                      return (
                        <div
                          key={index}
                          className={`border rounded-lg p-4 transition-colors ${
                            isSubmitted
                              ? isCorrect
                                ? "bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-700"
                                : "bg-red-50 border-red-300 dark:bg-red-950/30 dark:border-red-700"
                              : "bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30"
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <span className="bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 text-xs font-semibold px-2 py-1 rounded">
                              #{index + 1}
                            </span>
                            <span className="font-mono text-xl font-bold text-gray-900 dark:text-white">
                              {problem.problem}
                            </span>
                          </div>

                          {!isSubmitted ? (
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                placeholder="Enter your answer"
                                value={practiceAnswers[index] || ""}
                                onChange={(e) =>
                                  handlePracticeAnswerChange(
                                    index,
                                    e.target.value,
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "Enter" &&
                                    practiceAnswers[index]?.trim()
                                  ) {
                                    handleCheckAnswer(index, problem.solution);
                                  }
                                }}
                                className="font-mono text-lg"
                              />
                              <Button
                                onClick={() =>
                                  handleCheckAnswer(index, problem.solution)
                                }
                                disabled={!practiceAnswers[index]?.trim()}
                              >
                                Check
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                {isCorrect ? (
                                  <>
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                    <span className="font-semibold text-green-600">
                                      Correct!
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-5 h-5 text-red-600" />
                                    <span className="font-semibold text-red-600">
                                      Incorrect
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-400">
                                      - Your answer: {practiceAnswers[index]}
                                    </span>
                                  </>
                                )}
                              </div>

                              <div className="pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                                <p className="font-mono text-green-600 dark:text-green-400 font-bold">
                                  Answer: {problem.solution}
                                </p>
                                {problem.steps && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    <span className="font-semibold">
                                      Steps:{" "}
                                    </span>
                                    {problem.steps}
                                  </p>
                                )}
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRetryProblem(index)}
                                className="flex items-center gap-1"
                              >
                                <RotateCcw className="w-4 h-4" />
                                Try Again
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {isLessonMode && !isCompleted && (
              <div className="flex flex-col gap-4">
                <Button
                  size="lg"
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleCompleteLesson}
                  disabled={submitting}
                >
                  {submitting
                    ? "Completing..."
                    : "Complete Lesson & Unlock Next Step"}
                </Button>
                <p className="text-sm text-center text-muted-foreground">
                  Clicking this will mark the lesson as complete and unlock the
                  Forge practice.
                </p>
              </div>
            )}

            {isLessonMode && isCompleted && (
              <div className="flex flex-col gap-4">
                <div className="text-center text-green-600 font-semibold flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Lesson Completed
                </div>
                {/* Determine next destination */}
                <Button size="lg" className="w-full" onClick={handleContinue}>
                  Continue Journey <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Fallback / Generic Practice Link */}
            {!isLessonMode && (
              <Link to={`/practice?technique=${technique.id}`}>
                <Button
                  size="lg"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Practice This Technique
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Technique Chat Assistant */}
      <TechniqueChatTrigger />
      <TechniqueChatPanel />
    </TechniqueChatProvider>
  );
}
